import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Analytics from './Analytics';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageSubtitle,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Form,
  FormGroup,
  FormRow,
  Label,
  Input,
  Select,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableCell,
  Grid,
  StatusBadge,
  TabContainer,
  TabButton,
  ScrollableContainer,
  FixedHeaderTable
} from '../styles/AdminPortal.styles';
import { adminAPI } from '../services/api';
import { ConfirmationModal, SuccessModal, ErrorModal } from './Modal';

function AdminPortal() {
  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [shows, setShows] = useState([]);
  const [presentersList, setPresentersList] = useState([]);
  const [ussdCodes, setUssdCodes] = useState([]);
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState({});
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingUSSD, setEditingUSSD] = useState(null);
  const [editingIncentive, setEditingIncentive] = useState(null);
  
  // Pagination states for API
  const [currentApiPage, setCurrentApiPage] = useState(1);
  const [itemsPerApiPage] = useState(50); // Fetch 50 items at a time from backend
  const [totalItemsCount, setTotalItemsCount] = useState(0);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Client-side rendering limit (until backend implements pagination)
  const [displayLimit, setDisplayLimit] = useState(100); // Show only 100 items initially
  const [allItemsLoaded, setAllItemsLoaded] = useState([]); // Store all fetched items
  
  // Log render info
  console.log('🎨 [AdminPortal] Rendering with:', {
    loading,
    tabLoading,
    itemsCount: items.length,
    totalItemsCount,
    activeTab
  });
  
  // Pagination states for UI (client-side pagination of loaded items)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Load data based on active tab on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        console.log('⏱️ [AdminPortal] Starting initial data load...');
        // Don't use global loading - use tabLoading instead to allow navigation
        // Load data for the default active tab
        await refreshTabData(activeTab);
        console.log('✅ [AdminPortal] Initial data load complete');
      } catch (err) {
        console.error('❌ [AdminPortal] Error loading initial data:', err);
        setError('Failed to load data. Please try again.');
      }
    };

    // Set loading false immediately so UI is not blocked
    setLoading(false);
    loadInitialData();
  }, []); // Only run once on mount

  // Function to refresh data based on active tab
  const refreshTabData = async (tabName) => {
    try {
      console.log(`🔄 [refreshTabData] Starting refresh for ${tabName} tab...`);
      setTabLoading(prev => ({ ...prev, [tabName]: true }));
      
      switch (tabName) {
        case 'items':
          // For items tab, we need items, presenters, shows, and ussd codes for the form dropdowns
          console.log('📡 [Items Tab] Fetching data from APIs (with pagination)...');
          const startTime = performance.now();
          
          // Reset to first page when refreshing the tab
          setCurrentApiPage(1);
          
          const [itemsResponse, presentersResponse, showsResponse, ussdResponse] = await Promise.all([
            adminAPI.getItems(1, itemsPerApiPage), // Fetch first page with pagination
            adminAPI.getPresenters(),
            adminAPI.getRadioShows(),
            adminAPI.getUSSDCodes()
          ]);
          
          const fetchTime = performance.now() - startTime;
          console.log(`✅ [Items Tab] APIs responded in ${fetchTime.toFixed(2)}ms`);
          console.log(`📊 [Items Tab] Data sizes:`, {
            items: itemsResponse.data?.auctions?.length || 0,
            totalItems: itemsResponse.data?.total || itemsResponse.data?.auctions?.length || 0,
            presenters: presentersResponse.data?.presenters?.length || 0,
            shows: showsResponse.data?.radio_shows?.length || 0,
            ussd: ussdResponse.data?.ussd_codes?.length || 0
          });
          
          console.log('💾 [Items Tab] Setting state...');
          const stateStartTime = performance.now();
          
          // Set all state updates - React 18 will batch these automatically
          if (itemsResponse.data && itemsResponse.data.auctions) {
            const allItems = itemsResponse.data.auctions || [];
            console.log(`📦 [Items Tab] Received ${allItems.length} items from backend`);
            
            // Store all items
            setAllItemsLoaded(allItems);
            
            // Only display first 100 items initially for fast rendering
            const itemsToDisplay = allItems.slice(0, displayLimit);
            console.log(`🎨 [Items Tab] Rendering ${itemsToDisplay.length} of ${allItems.length} items`);
            setItems(itemsToDisplay);
            
            // Set total count if backend provides it
            const total = itemsResponse.data.total || allItems.length;
            setTotalItemsCount(total);
            
            // Check if backend has more items (for real pagination)
            setHasMoreItems(allItems.length === itemsPerApiPage);
          }
          if (presentersResponse.data) {
            setPresentersList(presentersResponse.data.presenters || []);
          }
          if (showsResponse.data) {
            setShows(showsResponse.data.radio_shows || []);
          }
          if (ussdResponse.data) {
            setUssdCodes(ussdResponse.data.ussd_codes || []);
          }
          
          const stateTime = performance.now() - stateStartTime;
          console.log(`✅ [Items Tab] State set in ${stateTime.toFixed(2)}ms`);
          break;
          
        case 'shows':
          // For shows tab, we need shows and presenters for the form dropdown
          const [showsResp, presentersResp] = await Promise.all([
            adminAPI.getRadioShows(),
            adminAPI.getPresenters()
          ]);
          
          if (showsResp.data) {
            setShows(showsResp.data.radio_shows || []);
          }
          if (presentersResp.data) {
            setPresentersList(presentersResp.data.presenters || []);
          }
          break;
          
        case 'presenters':
          // For presenters tab, we need presenters, shows, and USSD codes for assignment controls
          const [presentersResp2, showsResp2, ussdResp2] = await Promise.all([
            adminAPI.getPresenters(),
            adminAPI.getRadioShows(),
            adminAPI.getUSSDCodes()
          ]);
          
          if (presentersResp2.data) {
            setPresentersList(presentersResp2.data.presenters || []);
          }
          if (showsResp2.data) {
            setShows(showsResp2.data.radio_shows || []);
          }
          if (ussdResp2.data) {
            setUssdCodes(ussdResp2.data.ussd_codes || []);
          }
          break;
          
        case 'ussd':
          const ussdResp = await adminAPI.getUSSDCodes();
          if (ussdResp.data) {
            setUssdCodes(ussdResp.data.ussd_codes || []);
          }
          break;

        case 'incentives':
          const incentivesResp = await adminAPI.getIncentives();
          if (incentivesResp.data) {
            const rawIncentives = incentivesResp.data;
            const incentivesList = Array.isArray(rawIncentives)
              ? rawIncentives
              : (rawIncentives.incentives || rawIncentives.records || rawIncentives.data || []);
            setIncentives(incentivesList);
          }
          break;
          
        case 'overview':
          // For overview, we need items, shows, and presenters
          const [overviewItemsResponse, overviewShowsResponse, overviewPresentersResponse] = await Promise.all([
            adminAPI.getItems(),
            adminAPI.getRadioShows(),
            adminAPI.getPresenters()
          ]);
          
          if (overviewItemsResponse.data && overviewItemsResponse.data.auctions) {
            setItems(overviewItemsResponse.data.auctions || []);
          }
          if (overviewShowsResponse.data) {
            setShows(overviewShowsResponse.data.radio_shows || []);
          }
          if (overviewPresentersResponse.data) {
            setPresentersList(overviewPresentersResponse.data.presenters || []);
          }
          break;
          
        case 'analytics':
          // Analytics tab handles its own data loading
          break;
          
        default:
          break;
      }
    } catch (err) {
      console.error(`❌ [refreshTabData] Error refreshing ${tabName} data:`, err);
      setError(`Failed to refresh ${tabName} data. Please try again.`);
    } finally {
      console.log(`🏁 [refreshTabData] Setting tabLoading[${tabName}] to false`);
      setTabLoading(prev => ({ ...prev, [tabName]: false }));
    }
  };

  // Handle tab changes and refresh data
  const handleTabChange = (tabName) => {
    // Only refresh if switching to a different tab
    if (activeTab !== tabName) {
      setActiveTab(tabName);
      refreshTabData(tabName);
    }
  };

  // Load more items (client-side first, then API)
  const loadMoreItems = async () => {
    if (loadingMore) return;
    
    try {
      setLoadingMore(true);
      
      // Check if we have more items already loaded (client-side)
      const currentDisplayCount = items.length;
      const totalLoadedCount = allItemsLoaded.length;
      
      if (currentDisplayCount < totalLoadedCount) {
        // We have more items in memory, just display them
        console.log(`📦 [Load More] Showing more items from memory (${currentDisplayCount} → ${Math.min(currentDisplayCount + 100, totalLoadedCount)})`);
        const newDisplayLimit = currentDisplayCount + 100;
        const itemsToDisplay = allItemsLoaded.slice(0, newDisplayLimit);
        setItems(itemsToDisplay);
        setDisplayLimit(newDisplayLimit);
        
        console.log(`✅ [Load More] Now displaying ${itemsToDisplay.length} of ${totalLoadedCount} loaded items`);
      } else if (hasMoreItems) {
        // Need to fetch more from backend
        console.log(`📥 [Load More] Fetching page ${currentApiPage + 1} from backend...`);
        
        const nextPage = currentApiPage + 1;
        const response = await adminAPI.getItems(nextPage, itemsPerApiPage);
        
        if (response.data && response.data.auctions) {
          const newItems = response.data.auctions;
          console.log(`✅ [Load More] Loaded ${newItems.length} more items from backend`);
          
          // Append to all loaded items
          const updatedAllItems = [...allItemsLoaded, ...newItems];
          setAllItemsLoaded(updatedAllItems);
          
          // Display up to current limit
          const itemsToDisplay = updatedAllItems.slice(0, displayLimit + 100);
          setItems(itemsToDisplay);
          setDisplayLimit(displayLimit + 100);
          setCurrentApiPage(nextPage);
          
          // Check if there are more items to load from backend
          setHasMoreItems(newItems.length === itemsPerApiPage);
          
          // Update total count if provided
          if (response.data.total) {
            setTotalItemsCount(response.data.total);
          }
        }
      }
    } catch (err) {
      console.error('❌ [Load More] Error loading more items:', err);
      setErrorMessage('Failed to load more items. Please try again.');
      setShowErrorModal(true);
    } finally {
      setLoadingMore(false);
    }
  };

  // Form states
  const [itemForm, setItemForm] = useState({
    name: '',
    bidMinValue: '',
    bidMaxValue: '',
    radioShowId: '',
    amountPerBid: '',
    expectedReturns: '',
    bidEndDate: '',
    ussdCode: '',
    auctionFee: ''
  });

  // New form states for enhanced bidding items
  const [presenterSelection, setPresenterSelection] = useState({
    type: 'existing', // 'existing' or 'new'
    presenterId: '',
    newPresenter: {
      user_name: '',
      password: ''
    }
  });

  const [radioShowSelection, setRadioShowSelection] = useState({
    type: 'existing', // 'existing' or 'new'
    radioShowId: '',
    newRadioShow: {
      name: '',
      startTime: '',
      endTime: ''
    }
  });

  const [ussdSelection, setUssdSelection] = useState({
    type: 'existing', // 'existing' or 'new'
    ussdCode: '',
    newUssdCode: ''
  });

  // Password toggle states
  const [showPresenterPassword, setShowPresenterPassword] = useState(false);
  const [showNewPresenterPassword, setShowNewPresenterPassword] = useState(false);

  // Original form states (needed for other tabs)
  const [showForm, setShowForm] = useState({
    name: '',
    startTime: '',
    endTime: '',
    presenterId: '',
    status: 'active'
  });

  const [presenterForm, setPresenterForm] = useState({
    user_name: '',
    password: '',
    status: 'active',
    ussd_code_id: ''
  });

  const [ussdForm, setUssdForm] = useState({
    ussd_code: ''
  });

  const [incentiveForm, setIncentiveForm] = useState({
    msisdn: '',
    item_name: '',
    amount: ''
  });

  const getPresenterAssignedUSSDId = (presenter) => {
    return presenter?.assigned_ussd_code?.id || '';
  };

  const isUSSDTakenByAnotherPresenter = (ussdCodeId, currentPresenterId = null) => {
    if (!ussdCodeId) return false;

    return presentersList.some((presenter) => {
      if (!presenter?.id) return false;
      if (currentPresenterId && presenter.id === currentPresenterId) return false;

      return presenter?.assigned_ussd_code?.id === ussdCodeId;
    });
  };

  // Enhanced item submission handler
  const handleEnhancedItemSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading state
    setLoading(true);
    
    // Validate required fields
    if (!itemForm.name.trim()) {
      setErrorMessage('Item name is required');
      setShowErrorModal(true);
      return;
    }
    
    if (!itemForm.bidMinValue || !itemForm.bidMaxValue || !itemForm.amountPerBid || !itemForm.expectedReturns) {
      setErrorMessage('All bid amounts and pricing fields are required');
      setShowErrorModal(true);
      return;
    }
    
    if (!itemForm.bidEndDate) {
      setErrorMessage('Bid end date is required');
      setShowErrorModal(true);
      return;
    }
    
    // Validate presenter selection
    if (presenterSelection.type === 'existing' && !presenterSelection.presenterId) {
      setErrorMessage('Please select a presenter');
      setShowErrorModal(true);
      return;
    }
    
    if (presenterSelection.type === 'new' && (!presenterSelection.newPresenter.user_name.trim() || !presenterSelection.newPresenter.password)) {
      setErrorMessage('New presenter username and password are required');
      setShowErrorModal(true);
      return;
    }
    
    // Validate radio show selection
    if (radioShowSelection.type === 'existing' && !radioShowSelection.radioShowId) {
      setErrorMessage('Please select a radio show');
      setShowErrorModal(true);
      return;
    }
    
    if (radioShowSelection.type === 'new' && (!radioShowSelection.newRadioShow.name.trim() || !radioShowSelection.newRadioShow.startTime || !radioShowSelection.newRadioShow.endTime)) {
      setErrorMessage('New radio show name, start time, and end time are required');
      setShowErrorModal(true);
      return;
    }
    
    // Validate USSD selection (optional but if new is selected, code is required)
    if (ussdSelection.type === 'new' && !ussdSelection.newUssdCode.trim()) {
      setErrorMessage('New USSD code is required when creating a new one');
      setShowErrorModal(true);
      return;
    }
    
    try {
      // Prepare the complete data object to send to backend
      const completeItemData = {
        // Basic item information
        item_name: itemForm.name.trim(),
        min_bid_amount: parseFloat(itemForm.bidMinValue),
        max_bid_amount: parseFloat(itemForm.bidMaxValue),
        item_price: parseFloat(itemForm.amountPerBid),
        target: parseFloat(itemForm.expectedReturns),
        stop_time: itemForm.bidEndDate ? new Date(itemForm.bidEndDate).toISOString() : null,
        auction_fee: parseFloat(itemForm.auctionFee) || 0,
        
        // Presenter information
        presenter: presenterSelection.type === 'existing' ? {
          type: 'existing',
          presenter_id: presenterSelection.presenterId
        } : {
          type: 'new',
          user_name: presenterSelection.newPresenter.user_name.trim(),
          password: presenterSelection.newPresenter.password
        },
        
        // Radio show information
        radio_show: radioShowSelection.type === 'existing' ? {
          type: 'existing',
          radio_show_id: radioShowSelection.radioShowId
        } : {
          type: 'new',
          name: radioShowSelection.newRadioShow.name.trim(),
          start_time: radioShowSelection.newRadioShow.startTime,
          end_time: radioShowSelection.newRadioShow.endTime
        },
        
        // USSD information
        ussd: ussdSelection.type === 'existing' ? {
          type: 'existing',
          ussd_code: ussdSelection.ussdCode
        } : {
          type: 'new',
          ussd_code: ussdSelection.newUssdCode.trim()
        }
      };

      // Log the data being sent to backend for debugging
      console.log('Sending complete item data to backend:', completeItemData);
      
      // Send the complete data to the backend
      let response;
      if (editingItem) {
        // Update existing item
        const updateData = {
          id: editingItem.id,
          ...completeItemData
        };
        response = await adminAPI.updateItem(updateData);
      } else {
        // Create new item
        response = await adminAPI.addItem(completeItemData);
      }
      if (response.data) {
        // Reload all data to get updated lists
        const [itemsResponse, presentersResponse, showsResponse, ussdResponse] = await Promise.all([
          adminAPI.getItems(),
          adminAPI.getPresenters(),
          adminAPI.getRadioShows(),
          adminAPI.getUSSDCodes()
        ]);
        
        if (itemsResponse.data && itemsResponse.data.auctions) {
          setItems(itemsResponse.data.auctions || []);
        }
        if (presentersResponse.data) {
          setPresentersList(presentersResponse.data.presenters || []);
        }
        if (showsResponse.data) {
          setShows(showsResponse.data.radio_shows || []);
        }
        if (ussdResponse.data) {
          setUssdCodes(ussdResponse.data.ussd_codes || []);
        }
      }
      
      // Reset all forms
      setItemForm({
        name: '',
        bidMinValue: '',
        bidMaxValue: '',
        radioShowId: '',
        amountPerBid: '',
        expectedReturns: '',
        bidEndDate: '',
        ussdCode: '',
        auctionFee: ''
      });

      setPresenterSelection({
        type: 'existing',
        presenterId: '',
        newPresenter: {
          user_name: '',
          password: ''
        }
      });

      setRadioShowSelection({
        type: 'existing',
        radioShowId: '',
        newRadioShow: {
          name: '',
          startTime: '',
          endTime: ''
        }
      });

      setUssdSelection({
        type: 'existing',
        ussdCode: '',
        newUssdCode: ''
      });
      
      setSuccessMessage(editingItem ? 'Bidding item updated successfully!' : 'Bidding item created successfully!');
      setShowSuccessModal(true);
      
      // Clear editing state if updating
      if (editingItem) {
        setEditingItem(null);
      }
    } catch (err) {
      console.error('Error creating enhanced item:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Handle item form submission
  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      const itemData = {
        item_name: itemForm.name,
        min_bid_amount: parseFloat(itemForm.bidMinValue),
        max_bid_amount: parseFloat(itemForm.bidMaxValue),
        item_price: parseFloat(itemForm.amountPerBid),
        target: parseFloat(itemForm.expectedReturns),
        stop_time: itemForm.bidEndDate ? new Date(itemForm.bidEndDate).toISOString() : null,
        radio_show_id: itemForm.radioShowId,
        ussd_code: itemForm.ussdCode,
        auction_fee: parseFloat(itemForm.auctionFee) || 0
      };

      if (editingItem) {
        // Update existing item
        await handleUpdateItem(editingItem.id, itemData);
        setEditingItem(null);
      } else {
        // Create new item
        const response = await adminAPI.createItem(itemData);
        if (response.data) {
          // Reload items to get the updated list
          const itemsResponse = await adminAPI.getItems();
                  if (itemsResponse.data) {
          setItems(itemsResponse.data.auctions || []);
        }
        }
      }
      
      // Reset form
      setItemForm({
        name: '',
        bidMinValue: '',
        bidMaxValue: '',
        radioShowId: '',
        amountPerBid: '',
        expectedReturns: '',
        bidEndDate: '',
        ussdCode: '',
        auctionFee: ''
      });
      
      setSuccessMessage(editingItem ? 'Item updated successfully!' : 'Item added successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error saving item:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    }
  };

  // Handle radio show form submission
  const handleShowSubmit = async (e) => {
    e.preventDefault();
    
    // Validate time range
    if (showForm.startTime && showForm.endTime && showForm.startTime >= showForm.endTime) {
      setErrorMessage('End time must be after start time.');
      setShowErrorModal(true);
      return;
    }
    
    try {
      const showData = {
        name: showForm.name,
        start_time: showForm.startTime,
        end_time: showForm.endTime,
        presenter_id: showForm.presenterId
      };

      if (editingShow) {
        // Update existing show
        await handleUpdateShow(editingShow.id, showData);
        setEditingShow(null);
      } else {
        // Create new show
        const response = await adminAPI.createRadioShow(showData);
        if (response.data) {
          // Reload shows to get the updated list
          const showsResponse = await adminAPI.getRadioShows();
          if (showsResponse.data) {
            setShows(showsResponse.data.radio_shows || []);
          }
        }
      }
      
      // Reset form
      setShowForm({
        name: '',
        startTime: '',
        endTime: '',
        presenterId: '',
        status: 'active'
      });
      
      setSuccessMessage(editingShow ? 'Radio show updated successfully!' : 'Radio show added successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error saving radio show:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    }
  };

  // Handle presenter form submission
  const handlePresenterSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!presenterForm.user_name || presenterForm.user_name.trim().length < 3) {
      setErrorMessage('Username must be at least 3 characters long.');
      setShowErrorModal(true);
      return;
    }
    
    if (!editingPresenter && (!presenterForm.password || presenterForm.password.length < 6)) {
      setErrorMessage('Password must be at least 6 characters long.');
      setShowErrorModal(true);
      return;
    }

    if (editingPresenter && presenterForm.password && presenterForm.password.length < 6) {
      setErrorMessage('If provided, password must be at least 6 characters long.');
      setShowErrorModal(true);
      return;
    }
    
    // Check if username already exists (for new presenters only)
    if (!editingPresenter) {
      const existingPresenter = presentersList.find(p => p.user_name === presenterForm.user_name.trim());
      if (existingPresenter) {
        setErrorMessage('Username already exists. Please choose a different username.');
        setShowErrorModal(true);
        return;
      }
    }
    
    try {
      const presenterData = {
        user_name: presenterForm.user_name.trim()
      };

      if (!editingPresenter || presenterForm.password) {
        presenterData.password = presenterForm.password;
      }
      if (editingPresenter) {
        if (presenterForm.ussd_code_id === '__CLEAR__') {
          presenterData.ussd_code_id = null;
        } else if (presenterForm.ussd_code_id) {
          presenterData.ussd_code_id = presenterForm.ussd_code_id;
        }
      } else if (presenterForm.ussd_code_id) {
        presenterData.ussd_code_id = presenterForm.ussd_code_id;
      }

      if (editingPresenter) {
        // Update existing presenter
        await handleUpdatePresenter(editingPresenter.id, presenterData);
        setEditingPresenter(null);
      } else {
        // Create new presenter
        const response = await adminAPI.createPresenter(presenterData);
        if (response.data) {
          // Reload presenters to get the updated list
          const presentersResponse = await adminAPI.getPresenters();
          if (presentersResponse.data) {
            setPresentersList(presentersResponse.data.presenters || []);
          }
        }
      }
      
      // Reset form
      setPresenterForm({
        user_name: '',
        password: '',
        status: 'active',
        ussd_code_id: ''
      });
      
      setSuccessMessage(editingPresenter ? 'Presenter updated successfully!' : 'Presenter added successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error saving presenter:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function to format error messages
  const formatErrorMessage = (error) => {
    if (typeof error === 'string') return error;
    
    // Use the custom error message if available
    if (error.message) {
      return error.message;
    }
    
    // Fallback to generic message
    return 'An unexpected error occurred. Please try again.';
  };

  // Memoized sorted items to prevent re-sorting on every render
  const sortedRecentItems = React.useMemo(() => {
    console.log('🔢 [useMemo] Sorting items...');
    if (!items || items.length === 0) return [];
    
    const sorted = [...items].sort((a, b) => {
      const dateA = new Date(a.start_time || a.created_at || 0);
      const dateB = new Date(b.start_time || b.created_at || 0);
      return dateB - dateA; // Most recent first
    });
    
    console.log(`✅ [useMemo] Sorted ${sorted.length} items`);
    return sorted;
  }, [items]);

  // Helper function to get sorted items (for backward compatibility)
  const getSortedRecentItems = () => sortedRecentItems;

  // Memoized paginated items
  const paginatedItems = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedRecentItems.slice(startIndex, endIndex);
  }, [sortedRecentItems, currentPage, itemsPerPage]);

  // Get paginated items (for backward compatibility)
  const getPaginatedItems = () => paginatedItems;

  // Calculate total pages
  const getTotalPages = () => {
    return Math.ceil(sortedRecentItems.length / itemsPerPage);
  };

  // Pagination handlers
  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, getTotalPages()));
  };

  // Reset pagination when items change
  useEffect(() => {
    setCurrentPage(1);
  }, [items]);

  // Item management functions
  const handleEditItem = async (item) => {
    try {
      setLoading(true);
      
      // Fetch detailed item information
      const response = await adminAPI.getItem(item.id);
      
      if (response.data) {
        const itemData = response.data;
        setEditingItem(item);
        
        // Format date for datetime-local input (YYYY-MM-DDTHH:mm)
        let formattedEndDate = '';
        if (itemData.stop_time) {
          const date = new Date(itemData.stop_time);
          formattedEndDate = date.toISOString().slice(0, 16);
        }
        
        // Populate basic item form
        setItemForm({
          name: itemData.item_name || '',
          bidMinValue: itemData.min_bid_amount?.toString() || '',
          bidMaxValue: itemData.max_bid_amount?.toString() || '',
          radioShowId: itemData.radio_show?.radio_show_id || '',
          amountPerBid: itemData.item_price?.toString() || '',
          expectedReturns: itemData.target?.toString() || '',
          bidEndDate: formattedEndDate,
          ussdCode: itemData.ussd?.ussd_code || '',
          auctionFee: itemData.auction_fee?.toString() || ''
        });
        
        // Populate presenter selection
        if (itemData.presenter) {
          setPresenterSelection({
            type: 'existing',
            presenterId: itemData.presenter.presenter_id || '',
            newPresenter: {
              user_name: '',
              password: ''
            }
          });
        }
        
        // Populate radio show selection
        if (itemData.radio_show) {
          setRadioShowSelection({
            type: 'existing',
            radioShowId: itemData.radio_show.radio_show_id || '',
            newRadioShow: {
              name: '',
              startTime: '',
              endTime: ''
            }
          });
        }
        
        // Populate USSD selection
        if (itemData.ussd) {
          setUssdSelection({
            type: 'existing',
            ussdCode: itemData.ussd.ussd_code || '',
            newUssdCode: ''
          });
        }
        
        setActiveTab('items');
        refreshTabData('items');
      }
    } catch (err) {
      console.error('Error fetching item details:', err);
      setErrorMessage('Failed to load item details for editing. Please try again.');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setItemForm({
      name: '',
      bidMinValue: '',
      bidMaxValue: '',
      radioShowId: '',
      amountPerBid: '',
      expectedReturns: '',
      bidEndDate: '',
      ussdCode: '',
      auctionFee: ''
    });
    
    // Reset selection states
    setPresenterSelection({
      type: 'existing',
      presenterId: '',
      newPresenter: {
        user_name: '',
        password: ''
      }
    });

    setRadioShowSelection({
      type: 'existing',
      radioShowId: '',
      newRadioShow: {
        name: '',
        startTime: '',
        endTime: ''
      }
    });

    setUssdSelection({
      type: 'existing',
      ussdCode: '',
      newUssdCode: ''
    });
  };

  const handleDeleteItem = async (itemId) => {
    setDeleteTarget({ type: 'item', id: itemId });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (deleteTarget.type === 'item') {
        await adminAPI.deleteItem({ id: deleteTarget.id });
        // Reload items to get the updated list
        const response = await adminAPI.getItems();
        if (response.data && response.data.auctions) {
          setItems(response.data.auctions || []);
        }
        setSuccessMessage('Item deleted successfully!');
        setShowSuccessModal(true);
      } else if (deleteTarget.type === 'show') {
        await adminAPI.deleteRadioShow({ id: deleteTarget.id });
        // Reload shows to get the updated list
        const response = await adminAPI.getRadioShows();
        if (response.data) {
          setShows(response.data.radio_shows || []);
        }
        setSuccessMessage('Radio show deleted successfully!');
        setShowSuccessModal(true);
      } else if (deleteTarget.type === 'presenter') {
        await adminAPI.deletePresenter({ id: deleteTarget.id });
        // Reload presenters to get the updated list
        const response = await adminAPI.getPresenters();
        if (response.data) {
          setPresentersList(response.data.presenters || []);
        }
        setSuccessMessage('Presenter deleted successfully!');
        setShowSuccessModal(true);
      } else if (deleteTarget.type === 'ussd') {
        await adminAPI.deleteUSSDCode({ id: deleteTarget.id });
        // Reload USSD codes to get the updated list
        const response = await adminAPI.getUSSDCodes();
        if (response.data) {
          setUssdCodes(response.data.ussd_codes || []);
        }
        setSuccessMessage('USSD code deleted successfully!');
        setShowSuccessModal(true);
      } else if (deleteTarget.type === 'incentive') {
        await adminAPI.deleteIncentive({ id: deleteTarget.id });
        const response = await adminAPI.getIncentives();
        if (response.data) {
          const rawIncentives = response.data;
          const incentivesList = Array.isArray(rawIncentives)
            ? rawIncentives
            : (rawIncentives.incentives || rawIncentives.records || rawIncentives.data || []);
          setIncentives(incentivesList);
        }
        setSuccessMessage('Incentive deleted successfully!');
        setShowSuccessModal(true);
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleUpdateItem = async (itemId, updatedData) => {
    try {
      const itemData = {
        id: itemId,
        ...updatedData
      };
      
      await adminAPI.updateItem(itemData);
      // Reload items to get the updated list
      const response = await adminAPI.getItems();
      if (response.data && response.data.auctions) {
        setItems(response.data.auctions || []);
      }
      setSuccessMessage('Item updated successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error updating item:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    }
  };

  // Radio show management functions
  const handleEditShow = (show) => {
    setEditingShow(show);
    setShowForm({
      name: show.name,
      startTime: show.start_time || '',
      endTime: show.end_time || '',
      presenterId: show.presenter_id || '',
      status: show.status
    });
    setActiveTab('shows');
    refreshTabData('shows');
  };

  const handleCancelEditShow = () => {
    setEditingShow(null);
    setShowForm({
      name: '',
      startTime: '',
      endTime: '',
      presenterId: '',
      status: 'active'
    });
  };

  const handleDeleteShow = async (showId) => {
    setDeleteTarget({ type: 'show', id: showId });
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteShow = async () => {
    try {
      await adminAPI.deleteRadioShow({ id: deleteTarget.id });
      // Reload shows to get the updated list
      const response = await adminAPI.getRadioShows();
      if (response.data) {
        setShows(response.data.radio_shows || []);
      }
      setSuccessMessage('Radio show deleted successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error deleting show:', err);
      setErrorMessage('Failed to delete radio show. Please try again.');
      setShowErrorModal(true);
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleUpdateShow = async (showId, updatedData) => {
    try {
      const showData = {
        id: showId,
        ...updatedData
      };
      
      await adminAPI.updateRadioShow(showData);
      // Reload shows to get the updated list
      const response = await adminAPI.getRadioShows();
      if (response.data) {
        setShows(response.data.radio_shows || []);
      }
      setSuccessMessage('Radio show updated successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error updating show:', err);
      setErrorMessage('Failed to update radio show. Please try again.');
      setShowErrorModal(true);
    }
  };

  const [editingShow, setEditingShow] = useState(null);
  const [editingPresenter, setEditingPresenter] = useState(null);

  // Presenter management functions
  const handleEditPresenter = (presenter) => {
    setEditingPresenter(presenter);
    setPresenterForm({
      user_name: presenter.user_name || '',
      password: '', // Don't populate password for security
      status: presenter.status || 'active',
      ussd_code_id: getPresenterAssignedUSSDId(presenter)
    });
    setActiveTab('presenters');
    refreshTabData('presenters');
  };

  const handleCancelEditPresenter = () => {
    setEditingPresenter(null);
    setPresenterForm({
      user_name: '',
      password: '',
      status: 'active',
      ussd_code_id: ''
    });
  };

  const handleDeletePresenter = async (presenterId) => {
    setDeleteTarget({ type: 'presenter', id: presenterId });
    setShowDeleteModal(true);
  };

  const handleUpdatePresenter = async (presenterId, updatedData) => {
    try {
      const presenterData = {
        id: presenterId,
        ...updatedData
      };
      
      await adminAPI.updatePresenter(presenterData);
      // Reload presenters to get the updated list
      const response = await adminAPI.getPresenters();
      if (response.data) {
        setPresentersList(response.data.presenters || []);
      }
      setSuccessMessage('Presenter updated successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error updating presenter:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    }
  };

  // USSD management functions
  const handleUSSDSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUSSD) {
        // Update existing USSD code
        await handleUpdateUSSD(editingUSSD.id, { ussd_code: ussdForm.ussd_code });
        setEditingUSSD(null);
      } else {
        // Create new USSD code
        const response = await adminAPI.createUSSDCode({ ussd_code: ussdForm.ussd_code });
        if (response.data) {
          setUssdCodes(prev => [...prev, response.data.ussd_code]);
        }
        setSuccessMessage('USSD code added successfully!');
        setShowSuccessModal(true);
      }
      
      // Reset form
      setUssdForm({
        ussd_code: ''
      });
    } catch (err) {
      console.error('Error saving USSD code:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    }
  };

  const handleEditUSSD = (ussd) => {
    setEditingUSSD(ussd);
    setUssdForm({
      ussd_code: ussd.ussd_code
    });
    setActiveTab('ussd');
    refreshTabData('ussd');
  };

  const handleCancelEditUSSD = () => {
    setEditingUSSD(null);
    setUssdForm({
      ussd_code: ''
    });
  };

  const handleDeleteUSSD = async (ussdId) => {
    setDeleteTarget({ type: 'ussd', id: ussdId });
    setShowDeleteModal(true);
  };

  const handleUpdateUSSD = async (ussdId, updatedData) => {
    try {
      const response = await adminAPI.updateUSSDCode({ id: ussdId, ...updatedData });
      if (response.data) {
        // Update the USSD codes list with the new data
        setUssdCodes(prev => prev.map(ussd => 
          ussd.id === ussdId ? response.data.ussd_code : ussd
        ));
      }
      setSuccessMessage('USSD code updated successfully!');
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error updating USSD code:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    }
  };

  // Incentive management functions
  const handleIncentiveSubmit = async (e) => {
    e.preventDefault();
    try {
      const trimmedMsisdn = incentiveForm.msisdn.trim();
      const trimmedItemName = incentiveForm.item_name.trim();
      const parsedAmount = incentiveForm.amount === '' ? NaN : parseInt(incentiveForm.amount, 10);

      if (!trimmedMsisdn || !trimmedItemName || Number.isNaN(parsedAmount)) {
        setErrorMessage('MSISDN, item name, and amount are required.');
        setShowErrorModal(true);
        return;
      }

      const payload = {
        msisdn: trimmedMsisdn,
        item_name: trimmedItemName,
        amount: parsedAmount
      };

      if (editingIncentive) {
        await adminAPI.updateIncentive({ id: editingIncentive.id, ...payload });
        setSuccessMessage('Incentive updated successfully!');
      } else {
        await adminAPI.addIncentive(payload);
        setSuccessMessage('Incentive added successfully!');
      }

      const response = await adminAPI.getIncentives();
      if (response.data) {
        const rawIncentives = response.data;
        const incentivesList = Array.isArray(rawIncentives)
          ? rawIncentives
          : (rawIncentives.incentives || rawIncentives.records || rawIncentives.data || []);
        setIncentives(incentivesList);
      }

      setEditingIncentive(null);
      setIncentiveForm({
        msisdn: '',
        item_name: '',
        amount: ''
      });
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Error saving incentive:', err);
      setErrorMessage(formatErrorMessage(err));
      setShowErrorModal(true);
    }
  };

  const handleEditIncentive = (incentive) => {
    setEditingIncentive(incentive);
    setIncentiveForm({
      msisdn: incentive.msisdn || '',
      item_name: incentive.item_name || '',
      amount: incentive.amount !== undefined && incentive.amount !== null ? String(incentive.amount) : ''
    });
    setActiveTab('incentives');
    refreshTabData('incentives');
  };

  const handleCancelEditIncentive = () => {
    setEditingIncentive(null);
    setIncentiveForm({
      msisdn: '',
      item_name: '',
      amount: ''
    });
  };

  const handleDeleteIncentive = async (incentiveId) => {
    setDeleteTarget({ type: 'incentive', id: incentiveId });
    setShowDeleteModal(true);
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Admin Portal</PageTitle>
        <PageSubtitle>Manage bidding items, incentives, radio shows, and presenters</PageSubtitle>
      </PageHeader>

      {/* Tab Navigation */}
      <TabContainer>
        {[
          { key: 'items', label: 'Bidding Items' },
          { key: 'shows', label: 'Radio Shows' },
          { key: 'presenters', label: 'Presenters' },
          { key: 'ussd', label: 'USSD Codes' },
          { key: 'incentives', label: 'Incentives' },
          { key: 'analytics', label: 'Analytics' },
          { key: 'overview', label: 'Overview' }
        ].map(tab => (
          <TabButton
            key={tab.key}
            active={activeTab === tab.key}
            onClick={() => handleTabChange(tab.key)}
            disabled={tabLoading[tab.key]}
          >
            {tabLoading[tab.key] ? '⏳ ' : ''}{tab.label}
          </TabButton>
        ))}
      </TabContainer>

      {/* Add Bidding Item */}
      {activeTab === 'items' && (
        <Grid columns={2}>
          <Card>
            <CardHeader>
              <CardTitle>{editingItem ? 'Edit Bidding Item' : 'Add New Bidding Item'}</CardTitle>
              <CardDescription>
                {editingItem ? 'Update the bidding item details' : 'Create a new item for auction and assign it to a radio show'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form onSubmit={handleEnhancedItemSubmit} style={{ 
                maxWidth: '100%',
                '@media (max-width: 768px)': {
                  padding: '0.5rem'
                }
              }}>
                <div style={{ 
                  background: editingItem ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '1.5rem',
                  textAlign: 'center'
                }}>
                  <h3 style={{ margin: '0', fontSize: '1.2rem', fontWeight: '600' }}>
                    {editingItem ? '✏️ Edit Bidding Item' : '🚀 Enhanced Bidding Item Creation'}
                  </h3>
                  <p style={{ margin: '0.5rem 0 0 0', opacity: '0.9', fontSize: '0.9rem' }}>
                    {editingItem ? `Update item details with presenters, radio shows, and USSD codes` : 'Create items with presenters, radio shows, and USSD codes in one go'}
                  </p>
                  {editingItem && (
                    <div style={{ 
                      background: 'rgba(255, 255, 255, 0.2)', 
                      padding: '0.5rem', 
                      borderRadius: '4px', 
                      marginTop: '0.5rem',
                      fontSize: '0.9rem'
                    }}>
                      📝 Editing: <strong>{editingItem.item_name}</strong>
                    </div>
                  )}
                </div>
                <FormGroup>
                  <Label>Item Name *</Label>
                  <Input
                    type="text"
                    placeholder="Enter item name"
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    required
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label>Bid Min Value (KES) *</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={itemForm.bidMinValue}
                      onChange={(e) => setItemForm({ ...itemForm, bidMinValue: e.target.value })}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Bid Max Value (KES) *</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={itemForm.bidMaxValue}
                      onChange={(e) => setItemForm({ ...itemForm, bidMaxValue: e.target.value })}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <div style={{ 
                  borderLeft: '4px solid #667eea',
                  paddingLeft: '1rem',
                  marginBottom: '1rem',
                  background: 'rgba(102, 126, 234, 0.05)',
                  padding: '1rem',
                  borderRadius: '0 8px 8px 0'
                }}>
                  <h4 style={{ 
                    color: '#667eea', 
                    margin: '0 0 1rem 0', 
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}>
                    🎤 Presenter & Radio Show Configuration
                  </h4>
                <FormRow>
                  <FormGroup>
                    <Label>Presenter *</Label>
                    <Select
                      value={presenterSelection.type}
                      onChange={(e) => setPresenterSelection({ ...presenterSelection, type: e.target.value })}
                      required
                    >
                      <option value="existing">Existing Presenter</option>
                      <option value="new">New Presenter</option>
                    </Select>
                    {presenterSelection.type === 'new' && (
                      <>
                        <Input
                          type="text"
                          placeholder="Enter new presenter username"
                          value={presenterSelection.newPresenter.user_name}
                          onChange={(e) => setPresenterSelection({ ...presenterSelection, newPresenter: { ...presenterSelection.newPresenter, user_name: e.target.value } })}
                          required
                          style={{ marginTop: '0.5rem' }}
                        />
                        <div style={{ position: 'relative', marginTop: '0.5rem' }}>
                          <Input
                            type={showNewPresenterPassword ? "text" : "password"}
                            placeholder="Enter new presenter password"
                            value={presenterSelection.newPresenter.password}
                            onChange={(e) => setPresenterSelection({ ...presenterSelection, newPresenter: { ...presenterSelection.newPresenter, password: e.target.value } })}
                            required
                            style={{ paddingRight: '3rem' }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPresenterPassword(!showNewPresenterPassword)}
                            style={{
                              position: 'absolute',
                              right: '1rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              color: '#64748b',
                              fontSize: '1.1rem',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              zIndex: 10
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.color = '#2563eb';
                              e.target.style.background = 'rgba(37, 99, 235, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.color = '#64748b';
                              e.target.style.background = 'none';
                            }}
                          >
                            {showNewPresenterPassword ? '🙈' : '👁️'}
                          </button>
                        </div>
                      </>
                    )}
                    {presenterSelection.type === 'existing' && (
                      <Select
                        value={presenterSelection.presenterId}
                        onChange={(e) => setPresenterSelection({ ...presenterSelection, presenterId: e.target.value })}
                        required
                        style={{ marginTop: '0.5rem' }}
                      >
                        <option value="">Select a presenter</option>
                        {presentersList.length === 0 ? (
                          <option value="" disabled>No presenters available</option>
                        ) : (
                          presentersList.map(presenter => (
                            <option key={presenter.id} value={presenter.id}>
                              {presenter.user_name}
                            </option>
                          ))
                        )}
                      </Select>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label>Radio Show *</Label>
                    <Select
                      value={radioShowSelection.type}
                      onChange={(e) => setRadioShowSelection({ ...radioShowSelection, type: e.target.value })}
                      required
                    >
                      <option value="existing">Existing Radio Show</option>
                      <option value="new">New Radio Show</option>
                    </Select>
                    {radioShowSelection.type === 'new' && (
                      <>
                        <Input
                          type="text"
                          placeholder="Enter new show name"
                          value={radioShowSelection.newRadioShow.name}
                          onChange={(e) => setRadioShowSelection({ ...radioShowSelection, newRadioShow: { ...radioShowSelection.newRadioShow, name: e.target.value } })}
                          required
                          style={{ marginTop: '0.5rem' }}
                        />
                        <FormRow style={{ marginTop: '0.5rem' }}>
                          <FormGroup>
                            <Input
                              type="time"
                              value={radioShowSelection.newRadioShow.startTime}
                              onChange={(e) => setRadioShowSelection({ ...radioShowSelection, newRadioShow: { ...radioShowSelection.newRadioShow, startTime: e.target.value } })}
                              required
                            />
                          </FormGroup>
                          <FormGroup>
                            <Input
                              type="time"
                              value={radioShowSelection.newRadioShow.endTime}
                              onChange={(e) => setRadioShowSelection({ ...radioShowSelection, newRadioShow: { ...radioShowSelection.newRadioShow, endTime: e.target.value } })}
                              required
                            />
                          </FormGroup>
                        </FormRow>

                      </>
                    )}
                    {radioShowSelection.type === 'existing' && (
                      <Select
                        value={radioShowSelection.radioShowId}
                        onChange={(e) => setRadioShowSelection({ ...radioShowSelection, radioShowId: e.target.value })}
                        required
                        style={{ marginTop: '0.5rem' }}
                      >
                        <option value="">Select a radio show</option>
                        {shows.filter(show => show.status === 'active').length === 0 ? (
                          <option value="" disabled>No active radio shows available</option>
                        ) : (
                          shows.filter(show => show.status === 'active').map(show => (
                            <option key={show.id} value={show.id}>
                              {show.name} ({show.time_slot})
                            </option>
                          ))
                        )}
                      </Select>
                    )}
                  </FormGroup>
                </FormRow>
                </div>

                <FormRow>
                  <FormGroup>
                    <Label>Item Price (KES) *</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={itemForm.amountPerBid}
                      onChange={(e) => setItemForm({ ...itemForm, amountPerBid: e.target.value })}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Expected Returns (KES) *</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={itemForm.expectedReturns}
                      onChange={(e) => setItemForm({ ...itemForm, expectedReturns: e.target.value })}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Bid End Date *</Label>
                    <Input
                      type="datetime-local"
                      value={itemForm.bidEndDate}
                      onChange={(e) => setItemForm({ ...itemForm, bidEndDate: e.target.value })}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>USSD Code</Label>
                    <Select
                      value={ussdSelection.type}
                      onChange={(e) => setUssdSelection({ ...ussdSelection, type: e.target.value })}
                    >
                      <option value="existing">Existing USSD Code</option>
                      <option value="new">New USSD Code</option>
                    </Select>
                    {ussdSelection.type === 'new' && (
                      <Input
                        type="text"
                        placeholder="Enter new USSD code (e.g., *123#)"
                        value={ussdSelection.newUssdCode}
                        onChange={(e) => setUssdSelection({ ...ussdSelection, newUssdCode: e.target.value })}
                        required
                        style={{ marginTop: '0.5rem' }}
                      />
                    )}
                    {ussdSelection.type === 'existing' && (
                      <Select
                        value={ussdSelection.ussdCode}
                        onChange={(e) => setUssdSelection({ ...ussdSelection, ussdCode: e.target.value })}
                        style={{ marginTop: '0.5rem' }}
                      >
                        <option value="">Select a USSD code (optional)</option>
                        {ussdCodes.length === 0 ? (
                          <option value="" disabled>No USSD codes available</option>
                        ) : (
                          ussdCodes.map(ussd => (
                            <option key={ussd.id} value={ussd.ussd_code}>
                              {ussd.ussd_code}
                            </option>
                          ))
                        )}
                      </Select>
                    )}
                    <small style={{ color: '#666', fontSize: '0.8rem' }}>
                      Select a USSD code to associate with this item. This is optional.
                    </small>
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Auction Fee (KES)</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={itemForm.auctionFee}
                    onChange={(e) => setItemForm({ ...itemForm, auctionFee: e.target.value })}
                    min="0"
                    step="0.01"
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>
                    Enter the auction fee amount. This is optional.
                  </small>
                </FormGroup>

                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={loading}
                  style={{ 
                    width: '100%',
                    background: loading ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    padding: '1rem',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    borderRadius: '8px',
                    boxShadow: loading ? 'none' : '0 4px 15px rgba(102, 126, 234, 0.3)',
                    transition: 'all 0.3s ease',
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                    }
                  }}
                >
                  {loading ? '⏳ Processing...' : (editingItem ? '🔄 Update Bidding Item' : '✨ Create Enhanced Bidding Item')}
                </Button>
                
                {editingItem && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    style={{ width: '100%', marginTop: '1rem' }}
                    onClick={handleCancelEdit}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Bidding Items</CardTitle>
              <CardDescription>Overview of recently added items (sorted by start time, most recent first)</CardDescription>
            </CardHeader>
            <CardContent>
              {tabLoading['items'] ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem 2rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    border: '4px solid #e5e7eb',
                    borderTop: '4px solid #3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <p style={{ color: '#64748b', fontSize: '0.95rem' }}>Loading items...</p>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                  <p>{error}</p>
                  <Button onClick={() => window.location.reload()}>Retry</Button>
                </div>
              ) : (
                <>
                <FixedHeaderTable>
                  <div className="table-header">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell>Item Name</TableHeaderCell>
                          <TableHeaderCell>Start Time</TableHeaderCell>
                          <TableHeaderCell>Shortcode</TableHeaderCell>
                          <TableHeaderCell>Actions</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                    </Table>
                  </div>
                  <div className="table-body">
                    <Table>
                      <tbody>
                        {getSortedRecentItems().length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                              No items found. Create your first bidding item above.
                            </TableCell>
                          </TableRow>
                        ) : (
                          getPaginatedItems().map(item => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div>
                                  <strong>{item.item_name}</strong>
                                  <br />
                                  <small style={{ color: '#64748b' }}>
                                    {formatCurrency(item.item_price)} per bid
                                  </small>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div style={{ fontSize: '0.9rem' }}>
                                  {item.start_time ? formatDate(item.start_time) : 'Not set'}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: '500' }}>
                                  {item.ussd_code || item.ussd?.ussd_code || (
                                    <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>No shortcode</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                    onClick={() => handleEditItem(item)}
                                  >
                                    ✏️ Edit
                                  </Button>
                                  <Button 
                                    variant="danger" 
                                    size="sm" 
                                    style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                    onClick={() => handleDeleteItem(item.id)}
                                  >
                                    🗑️ Delete
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </FixedHeaderTable>
                
                {/* Pagination Controls */}
                {getSortedRecentItems().length > 0 && getTotalPages() > 1 && (
                  <div style={{ 
                    marginTop: '1rem', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.5rem 0',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, getSortedRecentItems().length)} of {getSortedRecentItems().length} items loaded
                      {totalItemsCount > items.length && (
                        <span style={{ color: '#3b82f6', fontWeight: '500' }}> ({totalItemsCount} total)</span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        style={{ 
                          fontSize: '0.8rem', 
                          padding: '0.25rem 0.5rem',
                          opacity: currentPage === 1 ? 0.5 : 1,
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        ← Previous
                      </Button>
                      <span style={{ fontSize: '0.9rem', color: '#64748b', margin: '0 0.5rem' }}>
                        Page {currentPage} of {getTotalPages()}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === getTotalPages()}
                        style={{ 
                          fontSize: '0.8rem', 
                          padding: '0.25rem 0.5rem',
                          opacity: currentPage === getTotalPages() ? 0.5 : 1,
                          cursor: currentPage === getTotalPages() ? 'not-allowed' : 'pointer'
                        }}
                      >
                        Next →
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Load More Button - Show more from memory or fetch from backend */}
                {(items.length < allItemsLoaded.length || hasMoreItems) && items.length > 0 && (
                  <div style={{ 
                    marginTop: '1rem', 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '1rem 0',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    <Button
                      variant="primary"
                      onClick={loadMoreItems}
                      disabled={loadingMore}
                      style={{
                        padding: '0.75rem 2rem',
                        fontSize: '0.95rem',
                        background: loadingMore ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: loadingMore ? 'not-allowed' : 'pointer',
                        boxShadow: loadingMore ? 'none' : '0 2px 8px rgba(59, 130, 246, 0.3)'
                      }}
                    >
                      {loadingMore ? '⏳ Loading More...' : `📥 Load More Items`}
                    </Button>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                      Showing {items.length} of {allItemsLoaded.length} items
                      {allItemsLoaded.length < totalItemsCount && ` (${totalItemsCount} total in database)`}
                    </div>
                  </div>
                )}
                
                {/* No more items message */}
                {items.length >= allItemsLoaded.length && !hasMoreItems && items.length > 0 && (
                  <div style={{ 
                    marginTop: '1rem', 
                    padding: '1rem',
                    textAlign: 'center',
                    color: '#64748b',
                    fontSize: '0.9rem',
                    borderTop: '1px solid #e2e8f0'
                  }}>
                    ✅ All {allItemsLoaded.length} items loaded and displayed
                  </div>
                )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Add Radio Show */}
      {activeTab === 'shows' && (
        <Grid columns={2}>
          <Card>
            <CardHeader>
              <CardTitle>{editingShow ? 'Edit Radio Show' : 'Add New Radio Show'}</CardTitle>
              <CardDescription>
                {editingShow ? 'Update the radio show details' : 'Create a new radio show and assign it to a presenter. Make sure to add presenters first in the Presenters tab.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form onSubmit={(e) => {
                e.preventDefault();
                if (editingShow) {
                  handleShowSubmit(e);
                } else {
                  handleShowSubmit(e);
                }
              }}>
                <FormGroup>
                  <Label>Show Name *</Label>
                  <Input
                    type="text"
                    placeholder="Enter show name"
                    value={showForm.name}
                    onChange={(e) => setShowForm({ ...showForm, name: e.target.value })}
                    required
                  />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label>Start Time *</Label>
                    <Input
                      type="time"
                      value={showForm.startTime}
                      onChange={(e) => setShowForm({ ...showForm, startTime: e.target.value })}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>End Time *</Label>
                    <Input
                      type="time"
                      value={showForm.endTime}
                      onChange={(e) => setShowForm({ ...showForm, endTime: e.target.value })}
                      required
                    />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label>Presenter *</Label>
                    <Select
                      value={showForm.presenterId}
                      onChange={(e) => setShowForm({ ...showForm, presenterId: e.target.value })}
                      required
                    >
                      <option value="">Select a presenter</option>
                      {presentersList.length === 0 ? (
                        <option value="" disabled>No presenters available. Please add a presenter first.</option>
                      ) : (
                        presentersList.map(presenter => (
                          <option key={presenter.id} value={presenter.id}>
                            {presenter.user_name}
                          </option>
                        ))
                      )}
                    </Select>
                  </FormGroup>
                </FormRow>

                <FormGroup>
                  <Label>Status *</Label>
                  <Select
                    value={showForm.status}
                    onChange={(e) => setShowForm({ ...showForm, status: e.target.value })}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                </FormGroup>

                <Button type="submit" variant="primary" style={{ width: '100%' }}>
                  {editingShow ? 'Update Radio Show' : 'Add Radio Show'}
                </Button>
                
                {editingShow && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    style={{ width: '100%', marginTop: '1rem' }}
                    onClick={handleCancelEditShow}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Radio Shows</CardTitle>
              <CardDescription>Manage existing radio shows</CardDescription>
            </CardHeader>
            <CardContent>
              {tabLoading['shows'] ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Loading radio shows...</p>
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                  <p>{error}</p>
                  <Button onClick={() => window.location.reload()}>Retry</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Show Name</TableHeaderCell>
                      <TableHeaderCell>Time Range</TableHeaderCell>
                      <TableHeaderCell>Presenter</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <tbody>
                    {shows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                          No radio shows found. Create your first radio show above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      shows.map(show => {
                        // Find the presenter for this show
                        const presenter = presentersList.find(p => p.id === show.presenter_id);
                        return (
                          <TableRow key={show.id}>
                            <TableCell>
                              <div>
                                <strong>{show.name}</strong>
                              </div>
                            </TableCell>
                            <TableCell>
                              {show.start_time && show.end_time ? (
                                <div>
                                  <strong>{show.start_time}</strong> - <strong>{show.end_time}</strong>
                                </div>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>No time set</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {presenter ? (
                                <div>
                                  <strong>{presenter.user_name}</strong>
                                </div>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>No presenter assigned</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={show.status || 'active'}>{show.status || 'active'}</StatusBadge>
                            </TableCell>
                            <TableCell>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  onClick={() => handleEditShow(show)}
                                >
                                  ✏️ Edit
                                </Button>
                                <Button 
                                  variant="danger" 
                                  size="sm" 
                                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  onClick={() => handleDeleteShow(show.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Add USSD Code */}
      {activeTab === 'ussd' && (
        <Grid columns={2}>
          <Card>
            <CardHeader>
              <CardTitle>{editingUSSD ? 'Edit USSD Code' : 'Add New USSD Code'}</CardTitle>
              <CardDescription>
                {editingUSSD ? 'Update USSD code details' : 'Create a new USSD code for the bidding system'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form onSubmit={handleUSSDSubmit}>
                <FormGroup>
                  <Label>USSD Code *</Label>
                  <Input
                    type="text"
                    placeholder="Enter USSD code (e.g., *123#)"
                    value={ussdForm.ussd_code}
                    onChange={(e) => setUssdForm({ ...ussdForm, ussd_code: e.target.value })}
                    required
                  />
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>
                    Enter the USSD code in the format *XXX# (e.g., *123#)
                  </small>
                </FormGroup>

                <Button type="submit" variant="primary" style={{ width: '100%' }}>
                  {editingUSSD ? 'Update USSD Code' : 'Add USSD Code'}
                </Button>
                
                {editingUSSD && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    style={{ width: '100%', marginTop: '1rem' }}
                    onClick={handleCancelEditUSSD}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All USSD Codes</CardTitle>
              <CardDescription>Manage USSD codes for the bidding system</CardDescription>
            </CardHeader>
            <CardContent>
              {tabLoading['ussd'] ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Loading USSD codes...</p>
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                  <p>{error}</p>
                  <Button onClick={() => window.location.reload()}>Retry</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>USSD Code</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <tbody>
                    {ussdCodes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} style={{ textAlign: 'center', padding: '2rem' }}>
                          No USSD codes found. Create your first USSD code above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      ussdCodes.map(ussd => (
                        <TableRow key={ussd.id}>
                          <TableCell>
                            <div>
                              <strong style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
                                {ussd.ussd_code}
                              </strong>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                onClick={() => handleEditUSSD(ussd)}
                              >
                                ✏️ Edit
                              </Button>
                              <Button 
                                variant="danger" 
                                size="sm" 
                                style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                onClick={() => handleDeleteUSSD(ussd.id)}
                              >
                                🗑️ Delete
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Incentives */}
      {activeTab === 'incentives' && (
        <Grid columns={2}>
          <Card>
            <CardHeader>
              <CardTitle>{editingIncentive ? 'Edit Incentive' : 'Add New Incentive'}</CardTitle>
              <CardDescription>
                {editingIncentive
                  ? 'Update incentive details'
                  : 'Create a new incentive record for a customer'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form onSubmit={handleIncentiveSubmit}>
                <FormRow>
                  <FormGroup>
                    <Label>MSISDN *</Label>
                    <Input
                      type="text"
                      placeholder="e.g. 2547XXXXXXXX"
                      value={incentiveForm.msisdn}
                      onChange={(e) => setIncentiveForm({ ...incentiveForm, msisdn: e.target.value })}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Item Name *</Label>
                    <Input
                      type="text"
                      placeholder="e.g. Shopping Voucher"
                      value={incentiveForm.item_name}
                      onChange={(e) => setIncentiveForm({ ...incentiveForm, item_name: e.target.value })}
                      required
                    />
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <Label>Amount *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="e.g. 500"
                    value={incentiveForm.amount}
                    onChange={(e) => setIncentiveForm({ ...incentiveForm, amount: e.target.value })}
                    required
                  />
                </FormGroup>
                <Button type="submit" variant="primary" style={{ width: '100%' }}>
                  {editingIncentive ? 'Update Incentive' : 'Add Incentive'}
                </Button>
                {editingIncentive && (
                  <Button
                    type="button"
                    variant="outline"
                    style={{ width: '100%', marginTop: '1rem' }}
                    onClick={handleCancelEditIncentive}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Incentives</CardTitle>
              <CardDescription>Manage incentive records</CardDescription>
            </CardHeader>
            <CardContent>
              {tabLoading['incentives'] ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Loading incentives...</p>
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                  <p>{error}</p>
                  <Button onClick={() => window.location.reload()}>Retry</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>MSISDN</TableHeaderCell>
                      <TableHeaderCell>Item Name</TableHeaderCell>
                      <TableHeaderCell>Amount</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <tbody>
                    {incentives.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>
                          No incentives found. Create your first incentive above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      incentives.map((incentive, index) => {
                        const amountValue = incentive.amount !== undefined && incentive.amount !== null
                          ? Number(incentive.amount)
                          : null;
                        return (
                          <TableRow key={incentive.id || `${incentive.msisdn || 'incentive'}-${index}`}>
                            <TableCell>
                              <div>
                                <strong style={{ fontFamily: 'monospace' }}>{incentive.msisdn || 'N/A'}</strong>
                              </div>
                            </TableCell>
                            <TableCell>{incentive.item_name || 'N/A'}</TableCell>
                            <TableCell>
                              {amountValue !== null && !Number.isNaN(amountValue)
                                ? formatCurrency(amountValue)
                                : 'N/A'}
                            </TableCell>
                            <TableCell>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  onClick={() => handleEditIncentive(incentive)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  onClick={() => handleDeleteIncentive(incentive.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Add Presenter */}
      {activeTab === 'presenters' && (
        <Grid columns={2}>
          <Card>
            <CardHeader>
              <CardTitle>{editingPresenter ? 'Edit Presenter' : 'Add New Presenter'}</CardTitle>
              <CardDescription>
                {editingPresenter ? 'Update presenter details' : 'Register a new radio presenter with username and password. Presenters can be assigned to radio shows when creating or editing shows.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form onSubmit={handlePresenterSubmit}>
                <FormRow>
                  <FormGroup>
                    <Label>Username *</Label>
                    <Input
                      type="text"
                      placeholder="Enter unique username (min 3 characters)"
                      value={presenterForm.user_name}
                      onChange={(e) => setPresenterForm({ ...presenterForm, user_name: e.target.value })}
                      required
                    />
                    <small style={{ color: '#666', fontSize: '0.8rem' }}>
                      Username must be unique and at least 3 characters long. This will be used for login.
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <Label>Password *</Label>
                    <div style={{ position: 'relative' }}>
                      <Input
                        type={showPresenterPassword ? "text" : "password"}
                        placeholder={editingPresenter ? "Leave blank to keep current password" : "Enter password (min 6 characters)"}
                        value={presenterForm.password}
                        onChange={(e) => setPresenterForm({ ...presenterForm, password: e.target.value })}
                        required={!editingPresenter}
                        style={{ paddingRight: '3rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPresenterPassword(!showPresenterPassword)}
                        style={{
                          position: 'absolute',
                          right: '1rem',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#64748b',
                          fontSize: '1.1rem',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          borderRadius: '4px',
                          transition: 'all 0.2s ease',
                          zIndex: 10
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#2563eb';
                          e.target.style.background = 'rgba(37, 99, 235, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#64748b';
                          e.target.style.background = 'none';
                        }}
                      >
                        {showPresenterPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                    <small style={{ color: '#666', fontSize: '0.8rem' }}>
                      {editingPresenter
                        ? 'Optional. If you enter a value, it must be at least 6 characters long.'
                        : 'Password must be at least 6 characters long.'}
                    </small>
                  </FormGroup>
                </FormRow>



                <FormGroup>
                  <Label>Status *</Label>
                  <Select
                    value={presenterForm.status}
                    onChange={(e) => setPresenterForm({ ...presenterForm, status: e.target.value })}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Assigned USSD Code</Label>
                  <Select
                    value={presenterForm.ussd_code_id}
                    onChange={(e) => setPresenterForm({ ...presenterForm, ussd_code_id: e.target.value })}
                  >
                    <option value="">{editingPresenter ? 'No shortcode selected' : 'No shortcode assigned'}</option>
                    {editingPresenter && <option value="__CLEAR__">Clear assignment (Unassign)</option>}
                    {ussdCodes.length === 0 ? (
                      <option value="" disabled>No USSD codes available</option>
                    ) : (
                      ussdCodes.map((ussd) => {
                        const takenByAnotherPresenter = isUSSDTakenByAnotherPresenter(ussd.id, editingPresenter?.id);
                        return (
                          <option key={ussd.id} value={ussd.id} disabled={takenByAnotherPresenter}>
                            {ussd.ussd_code}{takenByAnotherPresenter ? ' (already assigned)' : ''}
                          </option>
                        );
                      })
                    )}
                  </Select>
                  <small style={{ color: '#666', fontSize: '0.8rem' }}>
                    One presenter can only have one shortcode. Codes already assigned to another presenter are disabled.
                  </small>
                </FormGroup>

                <Button type="submit" variant="primary" style={{ width: '100%' }}>
                  {editingPresenter ? 'Update Presenter' : 'Add Presenter'}
                </Button>
                
                {editingPresenter && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    style={{ width: '100%', marginTop: '1rem' }}
                    onClick={handleCancelEditPresenter}
                  >
                    Cancel Edit
                  </Button>
                )}
              </Form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>All Presenters</CardTitle>
              <CardDescription>Manage radio presenters</CardDescription>
            </CardHeader>
            <CardContent>
              {tabLoading['presenters'] ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p>Loading presenters...</p>
                </div>
              ) : error ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                  <p>{error}</p>
                  <Button onClick={() => window.location.reload()}>Retry</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Username</TableHeaderCell>
                      <TableHeaderCell>Assigned Show</TableHeaderCell>
                      <TableHeaderCell>Assigned USSD Code</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <tbody>
                    {presentersList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                          No presenters found. Create your first presenter above.
                        </TableCell>
                      </TableRow>
                    ) : (
                      presentersList.map(presenter => {
                        // Find the radio show this presenter is assigned to
                        const assignedShow = shows.find(show => show.presenter_id === presenter.id);
                        return (
                          <TableRow key={presenter.id}>
                            <TableCell>
                              <div>
                                <strong>{presenter.user_name}</strong>
                              </div>
                            </TableCell>
                            <TableCell>
                              {assignedShow ? (
                                <div>
                                  <strong>{assignedShow.name}</strong>
                                  <br />
                                  <small style={{ color: '#666' }}>{assignedShow.time_slot}</small>
                                </div>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>Not assigned to any show</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {presenter.assigned_ussd_code ? (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    fontSize: '0.75rem',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '999px',
                                    backgroundColor: '#eff6ff',
                                    border: '1px solid #bfdbfe',
                                    color: '#1d4ed8',
                                    fontWeight: '600'
                                  }}
                                >
                                  {presenter.assigned_ussd_code.ussd_code}
                                </span>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>Unassigned</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <StatusBadge status={presenter.status || 'active'}>{presenter.status || 'active'}</StatusBadge>
                            </TableCell>
                            <TableCell>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  onClick={() => handleEditPresenter(presenter)}
                                >
                                  ✏️ Edit
                                </Button>
                                <Button 
                                  variant="danger" 
                                  size="sm" 
                                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                                  onClick={() => handleDeletePresenter(presenter.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Analytics */}
      {activeTab === 'analytics' && (
        <Analytics />
      )}

      {/* Overview */}
      {activeTab === 'overview' && (
        <div>
          <Grid columns={1}>
            <Card>
              <CardHeader>
                <CardTitle>All Bidding Items</CardTitle>
                <CardDescription>Complete overview of all bidding items in the system</CardDescription>
              </CardHeader>
              <CardContent>
                {tabLoading['overview'] ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <p>Loading items...</p>
                  </div>
                ) : error ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
                    <p>{error}</p>
                    <Button onClick={() => window.location.reload()}>Retry</Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell>Item Name</TableHeaderCell>
                        <TableHeaderCell>Radio Show</TableHeaderCell>
                        <TableHeaderCell>Presenter</TableHeaderCell>
                        <TableHeaderCell>Bid Range</TableHeaderCell>
                        <TableHeaderCell>Amount Per Bid</TableHeaderCell>
                        <TableHeaderCell>Expected Returns</TableHeaderCell>
                        <TableHeaderCell>End Date</TableHeaderCell>
                        <TableHeaderCell>Status</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <tbody>
                      {items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                            No bidding items found. Create your first item in the Bidding Items tab.
                          </TableCell>
                        </TableRow>
                      ) : (
                        items.map(item => {
                          // Find the radio show and presenter for this item
                          const radioShow = shows.find(show => show.id === item.radio_show_id);
                          const presenter = radioShow ? presentersList.find(p => p.id === radioShow.presenter_id) : null;
                          
                          return (
                            <TableRow key={item.id}>
                              <TableCell>
                                <div>
                                  <strong>{item.item_name}</strong>
                                </div>
                              </TableCell>
                              <TableCell>{item.radio_show_name || radioShow?.name || 'N/A'}</TableCell>
                              <TableCell>
                              {presenter ? (
                                <div>
                                  <strong>{presenter.user_name}</strong>
                                </div>
                              ) : (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>No presenter assigned</span>
                              )}
                            </TableCell>
                              <TableCell>
                                {formatCurrency(item.min_bid_amount)} - {formatCurrency(item.max_bid_amount)}
                              </TableCell>
                              <TableCell>{formatCurrency(item.item_price)}</TableCell>
                              <TableCell>{formatCurrency(item.target)}</TableCell>
                              <TableCell>
                                {item.stop_time || 'N/A'}
                              </TableCell>
                              <TableCell>
                                <StatusBadge status={item.status || 'active'}>{item.status || 'active'}</StatusBadge>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </Grid>
        </div>
      )}

      {/* Modals */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete this ${deleteTarget?.type || 'item'}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        buttonText="OK"
      />
    </PageContainer>
  );
}

export default AdminPortal; 
